/**
 * TransactionsPage — read-only list of the user's payment records.
 *
 * Route: `/transactions` (ProtectedRoute).
 *
 * Why this exists:
 *   The CheckoutReturnPage shows a receipt the moment a payment lands,
 *   but it's a one-time view. Without this page, the user has no way
 *   to look up past payments — especially their tracking numbers,
 *   which are the receipt-of-record for any disputes or refunds.
 *
 * Design choices:
 *   - **Rich row cards**, not summary + detail. Each card carries every
 *     piece of info from the receipt (amount, service, clinic, date,
 *     gateway, tracking). Mobile transactions lists are usually short
 *     enough that the density is fine, and it avoids a second route +
 *     fetch for what is essentially the same data.
 *   - **No filter / search.** MVP users have a handful of transactions;
 *     adding tabs for status (success / failed / pending) would clutter
 *     the page. Easy to add later if usage suggests it.
 *   - **`staleTime: 30s`** (vs 5min for services). A user who just paid
 *     expects their new transaction to appear promptly; services don't
 *     change that fast.
 *
 * Bilingual: `transaction.serviceName` is `{ fa, en }` — pass through
 * the same `localized()` helper services use.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Box,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

import LanguageToggle from '@/components/LanguageToggle';
import { transactionsApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { formatToman } from '@/utils/currency';
import { formatDate } from '@/utils/date';
import { localized } from '@/utils/localizedContent';

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

/**
 * Map backend status → MUI Chip color. Unknown statuses render neutral
 * so a future status the frontend doesn't recognize won't blow up.
 */
function statusChipColor(status) {
  switch (status) {
    case 'success': return 'success';
    case 'failed':  return 'error';
    case 'pending': return 'warning';
    default:        return 'default';
  }
}

function TransactionCard({ transaction, language, t, onContactSupport }) {
  const serviceName = localized(transaction.serviceName, language);

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Top row: status chip + amount */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Chip
            size="small"
            color={statusChipColor(transaction.status)}
            label={t(`transactions.status.${transaction.status}`, {
              defaultValue: transaction.status,
            })}
            variant="filled"
          />
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {formatToman(transaction.amount, language)}
          </Typography>
        </Stack>

        {/* Service name */}
        {serviceName && (
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            {serviceName}
          </Typography>
        )}

        {/* Clinic */}
        {transaction.clinicName && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ color: 'text.secondary', mb: 1 }}
          >
            <StorefrontIcon fontSize="small" />
            <Typography variant="body2">{transaction.clinicName}</Typography>
          </Stack>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Footer: paid date, gateway, tracking */}
        <Stack spacing={0.5} sx={{ color: 'text.secondary' }}>
          {transaction.paidAt && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <EventIcon fontSize="small" />
              <Typography variant="caption">
                {formatDate(transaction.paidAt, language, { style: 'datetime' })}
              </Typography>
            </Stack>
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            sx={{ gap: 1 }}
          >
            {transaction.gateway && (
              <Typography variant="caption">
                {t('transactions.row.gateway')}: {transaction.gateway}
              </Typography>
            )}
            {transaction.trackingNumber && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  // dir=ltr so digits read consistently in RTL too
                  direction: 'ltr',
                  unicodeBidi: 'isolate',
                }}
              >
                {t('transactions.row.trackingNumber')}: {transaction.trackingNumber}
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* Contact-support link — payments are ALWAYS handled by the APP
            support team (a clinic can't resolve a transaction), so this goes
            straight to a new app-support ticket linked to this transaction.
            No channel chooser here, unlike service history. */}
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="text"
            startIcon={<ContactSupportIcon fontSize="small" />}
            onClick={() => onContactSupport(transaction.id)}
          >
            {t('transactions.contactSupport')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */

export function TransactionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['transactions'],
    queryFn: () => transactionsApi.listTransactions(),
    // 30s — a user who just paid expects to see their new entry quickly.
    staleTime: 30 * 1000,
  });

  const transactions = data?.data?.transactions ?? [];

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <ReceiptLongIcon color="primary" />
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('transactions.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Subtitle (only when we have something to show — keeps empty
            and error states cleaner) */}
        {!isError && (transactions.length > 0 || isLoading) && (
          <Typography variant="body2" color="text.secondary">
            {t('transactions.subtitle')}
          </Typography>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={160}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        )}

        {/* Error */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('transactions.error.loadFailed')}
          </Alert>
        )}

        {/* Empty state */}
        {!isLoading && !isError && transactions.length === 0 && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <InboxIcon sx={{ fontSize: 56, opacity: 0.4 }} />
            <Typography variant="subtitle1" textAlign="center" fontWeight={600}>
              {t('transactions.empty.title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 320 }}
            >
              {t('transactions.empty.message')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/services')}
              sx={{ mt: 1 }}
            >
              {t('transactions.empty.browseServices')}
            </Button>
          </Stack>
        )}

        {/* List */}
        {!isLoading && !isError && transactions.length > 0 && (
          <Stack spacing={2}>
            {transactions.map((txn) => (
              <TransactionCard
                key={txn.id}
                transaction={txn}
                language={language}
                t={t}
                onContactSupport={(txnId) =>
                  navigate(
                    `/support/new?relatedType=transaction&relatedId=${txnId}`,
                  )
                }
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default TransactionsPage;
